import React, { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaMousePointer, FaCamera } from 'react-icons/fa';
import clsx from 'clsx';
import './HeatmapCanvas.css';

export const parseRatio = (val) => {
  if (typeof val !== 'number' || Number.isNaN(val)) return 0;
  if (val > 1) return Math.max(0, Math.min(1, val / 100));
  return Math.max(0, Math.min(1, val));
};

function createGradientPalette() {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const gradient = ctx.createLinearGradient(0, 0, 0, 256);
  gradient.addColorStop(0.0, 'rgba(0, 0, 255, 0)');
  gradient.addColorStop(0.2, 'rgba(0, 255, 255, 0.4)');
  gradient.addColorStop(0.4, 'rgba(0, 255, 0, 0.6)');
  gradient.addColorStop(0.7, 'rgba(255, 255, 0, 0.8)');
  gradient.addColorStop(1.0, 'rgba(255, 0, 0, 1.0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1, 256);

  return ctx.getImageData(0, 0, 1, 256).data;
}

export function getAncestorScroll(el, win, iframeDoc) {
  let scrollTop = 0;
  let scrollLeft = 0;
  let parent = el?.parentElement;

  while (parent && parent !== iframeDoc?.body && parent !== iframeDoc?.documentElement) {
    try {
      const style = win?.getComputedStyle ? win.getComputedStyle(parent) : parent.style;
      const overflowY = style?.overflowY || style?.overflow;
      const overflowX = style?.overflowX || style?.overflow;

      const isScrollableY =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight + 1;
      const isScrollableX =
        (overflowX === 'auto' || overflowX === 'scroll') &&
        parent.scrollWidth > parent.clientWidth + 1;

      if (isScrollableY && typeof parent.scrollTop === 'number') {
        scrollTop += parent.scrollTop;
      }
      if (isScrollableX && typeof parent.scrollLeft === 'number') {
        scrollLeft += parent.scrollLeft;
      }
    } catch (_) {}
    parent = parent.parentElement;
  }

  return { scrollTop, scrollLeft };
}

export function safeQuerySelector(root, selector) {
  if (!root || !selector || typeof selector !== 'string') return null;
  try {
    return root.querySelector(selector);
  } catch (_) {
    return null;
  }
}

/**
 * Progressively relaxes a CSS selector to locate the intended DOM element
 * without reliance on rigid coordinates or site-specific code.
 */
export function findMatchingElement(iframeDoc, pureSelector, item = {}) {
  if (!iframeDoc) return null;

  // 1. Try exact selector
  if (pureSelector) {
    const el = safeQuerySelector(iframeDoc, pureSelector);
    if (el) return el;
  }

  // Extract label / name from metadata (e.g. "...|label=Demo" or item.targetName)
  const rawSelector = item?.elementSelector || item?.selector || '';
  let metaLabel = null;
  if (rawSelector.includes('|')) {
    const parts = rawSelector.split('|').slice(1);
    for (const p of parts) {
      const trimmed = p.trim();
      if (trimmed.startsWith('label=')) {
        metaLabel = trimmed.slice(6).trim();
      } else if (trimmed.startsWith('text=')) {
        metaLabel = trimmed.slice(5).trim();
      }
    }
  }
  if (!metaLabel && item?.targetName && item.targetName !== 'Interactive Element') {
    metaLabel = item.targetName;
  }

  // 2. Try accessible attribute query if label is known
  if (metaLabel) {
    const escaped = metaLabel.replace(/["\\]/g, '\\$&');
    const byAria = safeQuerySelector(iframeDoc, `[aria-label="${escaped}"], [title="${escaped}"], [alt="${escaped}"]`);
    if (byAria) return byAria;
  }

  if (pureSelector) {
    // 3. Relax transient state classes and ordinal pseudo-classes (:nth-of-type, :nth-child)
    let relaxed = pureSelector
      .replace(/:(?:nth-of-type|nth-child)\([^)]*\)/gi, '')
      .replace(/:(?:first-child|last-child|first-of-type|last-of-type)/gi, '')
      .replace(/(?:\.cxAriaActive|\.undefined|\.active|\.is-active|\.hover|\.focus|\.selected|\.open|\.expanded)/gi, '')
      .replace(/:(?:hover|focus|focus-visible|active)/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (relaxed && relaxed !== pureSelector) {
      const el = safeQuerySelector(iframeDoc, relaxed);
      if (el) return el;
    }

    // 4. Loosen strict direct-child combinators ('>') to descendant combinators (' ')
    if (relaxed.includes('>')) {
      const descendantRelaxed = relaxed.replace(/\s*>\s*/g, ' ').replace(/\s+/g, ' ').trim();
      if (descendantRelaxed) {
        const el = safeQuerySelector(iframeDoc, descendantRelaxed);
        if (el) return el;
      }
    }

    // 5. Leaf element matching: extract the final target token in the chain
    const tokens = pureSelector.split(/[>\s]+/).map((t) => t.trim()).filter(Boolean);
    if (tokens.length > 1) {
      const leafToken = tokens[tokens.length - 1];
      const cleanLeaf = leafToken
        .replace(/:(?:nth-of-type|nth-child)\([^)]*\)/gi, '')
        .replace(/(?:\.cxAriaActive|\.undefined|\.active|\.is-active|\.hover|\.focus|\.selected|\.open|\.expanded)/gi, '')
        .trim();

      if (cleanLeaf) {
        // Try container + leaf (e.g. root container token + clean leaf token)
        const rootToken = tokens[0].replace(/:(?:nth-of-type|nth-child)\([^)]*\)/gi, '').trim();
        if (rootToken) {
          const containerPlusLeaf = safeQuerySelector(iframeDoc, `${rootToken} ${cleanLeaf}`);
          if (containerPlusLeaf) return containerPlusLeaf;
        }

        // Try leaf directly if it has an id, specific tag, or class
        if (cleanLeaf.includes('.') || cleanLeaf.includes('#')) {
          const leafEl = safeQuerySelector(iframeDoc, cleanLeaf);
          if (leafEl) return leafEl;
        }
      }
    }
  }

  // 6. Text content fallback if metaLabel exists and is short (< 50 chars)
  if (metaLabel && metaLabel.length > 1 && metaLabel.length < 50) {
    try {
      const searchTag = pureSelector ? pureSelector.split(/[>\s.:#]/)[0] : '';
      const candidateElements = iframeDoc.querySelectorAll(searchTag || 'a, button, span, div, p, label');
      for (let i = 0; i < candidateElements.length; i++) {
        const el = candidateElements[i];
        if (el.textContent && el.textContent.trim() === metaLabel) {
          return el;
        }
      }
    } catch (_) {}
  }

  return null;
}

/**
 * Retrieves an effective bounding client rect for an element, unboxing zero-dimension
 * wrappers (e.g. custom web components or elements with display: contents) by inspecting children.
 */
export function getEffectiveBoundingRect(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;

  const rect = el.getBoundingClientRect();
  if (rect && rect.width > 0 && rect.height > 0) {
    return { el, rect };
  }

  // If the element has 0 width or 0 height, inspect visible children
  if (el.children && el.children.length > 0) {
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i];
      if (child && typeof child.getBoundingClientRect === 'function') {
        const childRect = child.getBoundingClientRect();
        if (childRect && childRect.width > 0 && childRect.height > 0) {
          return { el: child, rect: childRect };
        }
      }
    }
  }

  return { el, rect };
}

/**
 * Detects if an element is occluded/covered by an overlapping element
 * (e.g. sticky/fixed headers, dropdowns, or higher z-index bars).
 */
export function isElementOccluded(el, iframeDoc) {
  if (!el || !iframeDoc || typeof iframeDoc.elementFromPoint !== 'function') return false;
  try {
    const rect = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (!rect || rect.width <= 0 || rect.height <= 0) return true;

    // Sample points on the element: center, top-left offset, bottom-right offset
    const testPoints = [
      { x: Math.round(rect.left + rect.width * 0.5), y: Math.round(rect.top + rect.height * 0.5) },
      { x: Math.round(rect.left + Math.min(8, rect.width * 0.2)), y: Math.round(rect.top + Math.min(8, rect.height * 0.2)) },
      { x: Math.round(rect.right - Math.min(8, rect.width * 0.2)), y: Math.round(rect.bottom - Math.min(8, rect.height * 0.2)) }
    ];

    let anyPointVisible = false;
    for (const pt of testPoints) {
      if (pt.x <= 0 || pt.y <= 0) continue;
      const hit = iframeDoc.elementFromPoint(pt.x, pt.y);
      if (!hit) continue;
      // If the top element under this point is the element itself or within its subtree, it's visible
      if (hit === el || el.contains(hit) || hit.contains(el)) {
        anyPointVisible = true;
        break;
      }
    }

    // If none of the sample points hit the element hierarchy, another element is covering it
    return !anyPointVisible;
  } catch (_) {
    return false;
  }
}

/**
 * Calculates the visible bounded rect for an element inside scrollable ancestors,
 * clamping box coordinates and detecting when an element is occluded or scrolled out.
 */
export function getClampedElementRect(el, iframeDoc) {
  if (!el || !iframeDoc) return null;
  const win = iframeDoc.defaultView || (iframeDoc.ownerDocument && iframeDoc.ownerDocument.defaultView) || null;
  if (!el.getBoundingClientRect) return null;
  const elRect = el.getBoundingClientRect();

  if (elRect.width <= 0 && elRect.height <= 0) {
    return { rect: elRect, isClipped: true };
  }

  // Viewport bounds
  const vpHeight = win?.innerHeight || iframeDoc.documentElement?.clientHeight || 1080;
  const vpWidth = win?.innerWidth || iframeDoc.documentElement?.clientWidth || 1920;

  let left = typeof elRect.left === 'number' ? elRect.left : 0;
  let top = typeof elRect.top === 'number' ? elRect.top : 0;
  let right = typeof elRect.right === 'number' ? elRect.right : (left + (elRect.width || 0));
  let bottom = typeof elRect.bottom === 'number' ? elRect.bottom : (top + (elRect.height || 0));

  let parent = el.parentElement;
  while (parent && parent !== iframeDoc.body && parent !== iframeDoc.documentElement) {
    try {
      const style = win?.getComputedStyle ? win.getComputedStyle(parent) : parent.style;
      const overflowY = style?.overflowY || style?.overflow;
      const overflowX = style?.overflowX || style?.overflow;

      const isScrollable =
        overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden' ||
        overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'hidden';

      if (isScrollable && parent.getBoundingClientRect) {
        const parentRect = parent.getBoundingClientRect();
        const pLeft = typeof parentRect.left === 'number' ? parentRect.left : 0;
        const pTop = typeof parentRect.top === 'number' ? parentRect.top : 0;
        const pRight = typeof parentRect.right === 'number' ? parentRect.right : (pLeft + (parentRect.width || 0));
        const pBottom = typeof parentRect.bottom === 'number' ? parentRect.bottom : (pTop + (parentRect.height || 0));

        // If element is scrolled out beyond container top/bottom/left/right:
        if (
          bottom <= pTop + 2 ||
          top >= pBottom - 2 ||
          right <= pLeft + 2 ||
          left >= pRight - 2
        ) {
          return { rect: elRect, isClipped: true };
        }

        // Clamp coordinates to container boundaries
        left = Math.max(left, pLeft);
        top = Math.max(top, pTop);
        right = Math.min(right, pRight);
        bottom = Math.min(bottom, pBottom);
      }
    } catch (_) {}
    parent = parent.parentElement;
  }

  // Check if outside iframe viewport
  if (bottom <= 2 || top >= vpHeight - 2 || right <= 2 || left >= vpWidth - 2) {
    return { rect: elRect, isClipped: true };
  }

  const origHeight = elRect.height || (bottom - top);
  const clampedWidth = right - left;
  const clampedHeight = bottom - top;

  // If clipped down to less than 10px height or less than 35% of original height:
  if (clampedWidth < 10 || clampedHeight < 8 || (origHeight > 0 && clampedHeight < origHeight * 0.35)) {
    return { rect: elRect, isClipped: true };
  }

  // Check if occluded by sticky or fixed elements
  if (isElementOccluded(el, iframeDoc)) {
    return { rect: elRect, isClipped: true };
  }

  return {
    rect: {
      left: Math.round(left),
      top: Math.round(top),
      width: Math.round(clampedWidth),
      height: Math.round(clampedHeight),
      right: Math.round(right),
      bottom: Math.round(bottom)
    },
    isClipped: false
  };
}

/**
 * Determines if an element is scrolled out of view by any of its scrollable/clipping ancestors
 * or occluded by sticky/fixed headers.
 */
export function isElementClippedByAncestors(el, iframeDoc) {
  const result = getClampedElementRect(el, iframeDoc);
  return result ? result.isClipped : false;
}

export function resolveElementCoordinates(iframeDoc, item, nativeWidth = 1920, nativeHeight = 1080) {
  if (!item) return null;

  const rawSelector = item.elementSelector || item.selector;
  const pureSelector = rawSelector ? String(rawSelector).split('|')[0].trim() : null;

  const offsetXRatio = parseRatio(item.offsetXRatio);
  const offsetYRatio = parseRatio(item.offsetYRatio);

  let targetEl = null;
  if (iframeDoc) {
    targetEl = findMatchingElement(iframeDoc, pureSelector, item);
  }

  if (targetEl) {
    try {
      const effective = getEffectiveBoundingRect(targetEl);
      const rect = effective?.rect;
      const activeEl = effective?.el || targetEl;

      if (rect && (rect.width > 0 || rect.height > 0 || rect.top !== 0 || rect.left !== 0)) {
        const win = iframeDoc.defaultView || (iframeDoc.ownerDocument && iframeDoc.ownerDocument.defaultView) || null;
        const docScrollTop = win?.scrollY || iframeDoc.documentElement?.scrollTop || iframeDoc.body?.scrollTop || 0;
        const docScrollLeft = win?.scrollX || iframeDoc.documentElement?.scrollLeft || iframeDoc.body?.scrollLeft || 0;

        const { scrollTop, scrollLeft } = getAncestorScroll(activeEl, win, iframeDoc);

        const clampedInfo = getClampedElementRect(activeEl, iframeDoc);
        const effectiveRect = clampedInfo?.rect || rect;
        const isClipped = clampedInfo ? clampedInfo.isClipped : false;

        const x = effectiveRect.left + docScrollLeft + (effectiveRect.width * offsetXRatio) + scrollLeft;
        const y = effectiveRect.top + docScrollTop + (effectiveRect.height * offsetYRatio) + scrollTop;

        const res = {
          x: Math.round(x),
          y: Math.round(y),
          width: Math.round(effectiveRect.width),
          height: Math.round(effectiveRect.height),
          matched: true
        };

        // Attach non-enumerable live viewport and clipping metadata for dynamic scroll rendering
        Object.defineProperty(res, 'targetEl', { value: activeEl, writable: true, configurable: true });
        Object.defineProperty(res, 'viewportRect', { value: effectiveRect, writable: true, configurable: true });
        Object.defineProperty(res, 'isClipped', {
          value: isClipped,
          writable: true,
          configurable: true
        });

        return res;
      }
    } catch (_) {}
  }

  // Fallback 1: Use explicit pageX / pageY if available
  if (typeof item.pageX === 'number' && typeof item.pageY === 'number') {
    return {
      x: Math.round(item.pageX),
      y: Math.round(item.pageY),
      width: 0,
      height: 0,
      matched: false
    };
  }

  // Fallback 2: Use xPercent / yPercent if available
  if (typeof item.xPercent === 'number' && typeof item.yPercent === 'number') {
    return {
      x: Math.round((item.xPercent / 100) * nativeWidth),
      y: Math.round((item.yPercent / 100) * nativeHeight),
      width: 0,
      height: 0,
      matched: false
    };
  }

  return null;
}

export default function HeatmapCanvas({
  density = [],
  radius = 24,
  blur = 16,
  opacity = 0.7,
  backgroundImage = null,
  targetAppUrl = null,
  htmlSnapshot = null,
  snapshot = null,
  mode = 'density',
  scrollReach = null,
  hoverDwell = null,
  attention = null,
  totalInteractions = 0,
  isInteractiveMode = false,
  iframeKey = 0,
  width = 960,
  height = 540,
  isLoading = false
}) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const iframeRef = useRef(null);
  const overlayLayerRef = useRef(null);
  const scrollOffsetRef = useRef(0);
  const canvasRef = useRef(null);
  const [activeHoveredElement, setActiveHoveredElement] = useState(null);
  const [isOffscreenTrayOpen, setIsOffscreenTrayOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(1280);
  const [iframeLoadedVersion, setIframeLoadedVersion] = useState(0);
  const [scrollVersion, setScrollVersion] = useState(0);
  const palette = useMemo(() => createGradientPalette(), []);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const cw = containerRef.current.clientWidth;
        if (cw > 0) setContainerWidth(cw);
      }
    };
    updateSize();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateSize)
      : null;
    if (ro && containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', updateSize);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const rawHtml = snapshot?.htmlSnapshot || htmlSnapshot;
  const effectiveUrl = snapshot?.url || targetAppUrl;

  const unrolledHtml = useMemo(() => {
    if (!rawHtml) return null;
    const interactionCss = !isInteractiveMode ? `
        input, select, textarea, button, a, label,
        [contenteditable],
        [role="button"],
        [role="combobox"],
        [role="listbox"],
        [role="option"],
        [role="menuitem"],
        [role="checkbox"],
        [role="radio"],
        [role="switch"],
        [role="tab"] {
          pointer-events: none !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          cursor: default !important;
        }
    ` : '';
    const unrollStyle = `
      <style>
        html, body {
          overflow: visible !important;
          height: auto !important;
          min-height: 100% !important;
          user-select: none !important;
          -webkit-user-select: none !important;
        }

        [class*="h-screen"],
        [class*="max-h-screen"] {
          height: auto !important;
          min-height: 100vh !important;
          max-height: none !important;
        }

        [class*="overflow-auto"],
        [class*="overflow-y-auto"],
        [class*="overflow-scroll"],
        [class*="overflow-hidden"],
        main {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
        }

        /* Universal Playback Normalization: unroll entrance animations */
        .elementor-invisible,
        [data-aos],
        .wow,
        .animated,
        [data-settings*="animation"],
        .elementor-motion-effects-element {
          visibility: visible !important;
          opacity: 1 !important;
          animation: none !important;
          transform: none !important;
          transition: none !important;
        }

        /* Universal SVG icon safeguard */
        svg {
          overflow: visible !important;
        }
        ${interactionCss}
      </style>
    `;
    return rawHtml.includes('</head>')
      ? rawHtml.replace('</head>', `${unrollStyle}</head>`)
      : `${unrollStyle}${rawHtml}`;
  }, [rawHtml, isInteractiveMode]);

  const hasSnapshot = Boolean(unrolledHtml || effectiveUrl || backgroundImage);

  const nativeWidth = Number(snapshot?.pageWidth) || Number(snapshot?.viewportWidth) || 1920;
  const nativeHeight = Number(snapshot?.pageHeight) || Number(snapshot?.viewportHeight) || height || 1080;

  const [contentHeight, setContentHeight] = useState(nativeHeight);

  useEffect(() => {
    setContentHeight(nativeHeight);
  }, [nativeHeight]);

  const densityRef = useRef(density);
  densityRef.current = density;

  const contentHeightRef = useRef(contentHeight);
  contentHeightRef.current = contentHeight;

  const nativeWidthRef = useRef(nativeWidth);
  nativeWidthRef.current = nativeWidth;

  const radiusRef = useRef(radius);
  radiusRef.current = radius;

  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    let isMounted = true;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handlePreventInteraction = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === 'select' ||
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'button' ||
        tagName === 'a' ||
        tagName === 'label' ||
        target?.isContentEditable ||
        target?.getAttribute?.('role') === 'button' ||
        target?.getAttribute?.('role') === 'combobox' ||
        target?.getAttribute?.('role') === 'listbox' ||
        target?.getAttribute?.('role') === 'option'
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    let scrollRafId = null;
    const handleScroll = (e) => {
      try {
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!doc) return;

        // In document-wide canvas modes (scroll reach and attention), track window scroll:
        if (modeRef.current === 'scroll' || modeRef.current === 'attention') {
          const winScrollTop = win?.scrollY || doc.documentElement?.scrollTop || doc.body?.scrollTop || 0;
          scrollOffsetRef.current = winScrollTop;
          if (overlayLayerRef.current) {
            overlayLayerRef.current.style.transform = `translateY(-${winScrollTop}px)`;
          }
        } else if (overlayLayerRef.current && overlayLayerRef.current.style.transform) {
          // In density mode, individual elements are positioned by their live viewport rects.
          // Reset any global transform so non-scrolling elements remain fixed.
          overlayLayerRef.current.style.transform = '';
        }

        // Trigger dynamic element re-synchronization on scroll
        if (scrollRafId) return;
        if (typeof requestAnimationFrame === 'function') {
          scrollRafId = requestAnimationFrame(() => {
            scrollRafId = null;
            if (isMounted) {
              setScrollVersion((v) => (v + 1) % 1000000);
            }
          });
        } else if (isMounted) {
          setScrollVersion((v) => (v + 1) % 1000000);
        }
      } catch (_) {}
    };

    const handleIframeMouseMove = () => {
      // Non-element coordinate hover is superseded by DOM element hover
    };

    const handleIframeMouseLeave = () => {
      setActiveHoveredElement(null);
    };

    const attachIframeListeners = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;

        if (!isInteractiveMode) {
          const styleId = 'heatmap-disable-interactions';
          if (!doc.getElementById(styleId)) {
            const styleEl = doc.createElement('style');
            styleEl.id = styleId;
            styleEl.textContent = `
              input, select, textarea, button, a, label,
              [contenteditable],
              [role="button"],
              [role="combobox"],
              [role="listbox"],
              [role="option"],
              [role="menuitem"],
              [role="checkbox"],
              [role="radio"],
              [role="switch"],
              [role="tab"] {
                pointer-events: none !important;
                user-select: none !important;
                -webkit-user-select: none !important;
                cursor: default !important;
              }
            `;
            doc.head?.appendChild(styleEl);
          }

          doc.querySelectorAll('input, select, textarea').forEach((el) => {
            el.setAttribute('tabindex', '-1');
            if (el.tagName.toLowerCase() === 'select') {
              el.style.pointerEvents = 'none';
            }
          });

          doc.addEventListener('click', handlePreventInteraction, { capture: true });
          doc.addEventListener('submit', handlePreventInteraction, { capture: true });
          doc.addEventListener('mousedown', handleMouseDown, { capture: true });
          doc.addEventListener('keydown', handlePreventInteraction, { capture: true });
        }

        doc.addEventListener('mousemove', handleIframeMouseMove, { passive: true });
        doc.addEventListener('mouseleave', handleIframeMouseLeave);

        doc.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        iframe.contentWindow?.addEventListener('scroll', handleScroll, { passive: true });

        const fullHeight = Math.max(
          doc.documentElement?.scrollHeight || 0,
          doc.body?.scrollHeight || 0,
          doc.documentElement?.offsetHeight || 0,
          nativeHeight
        );
        if (isMounted) {
          if (fullHeight > 0) {
            setContentHeight(fullHeight);
          }
          setIframeLoadedVersion((v) => v + 1);
        }
      } catch (_) {}
    };

    iframe.addEventListener('load', attachIframeListeners);
    if (iframe.contentDocument?.readyState === 'complete') {
      attachIframeListeners();
    }

    return () => {
      isMounted = false;
      if (scrollRafId && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(scrollRafId);
      }
      iframe.removeEventListener('load', attachIframeListeners);
      try {
        const doc = iframe.contentDocument;
        if (doc) {
          doc.removeEventListener('click', handlePreventInteraction, { capture: true });
          doc.removeEventListener('submit', handlePreventInteraction, { capture: true });
          doc.removeEventListener('mousedown', handleMouseDown, { capture: true });
          doc.removeEventListener('keydown', handlePreventInteraction, { capture: true });
          doc.removeEventListener('mousemove', handleIframeMouseMove);
          doc.removeEventListener('mouseleave', handleIframeMouseLeave);
          doc.removeEventListener('scroll', handleScroll, { capture: true });
        }
        iframe.contentWindow?.removeEventListener('scroll', handleScroll);
      } catch (_) {}
    };
  }, [nativeHeight, iframeKey, isInteractiveMode]);

  const scale = Math.min(1, containerWidth > 0 ? containerWidth / nativeWidth : 1);

  const maxCount = useMemo(() => {
    if (!Array.isArray(density) || density.length === 0) return 1;
    return Math.max(1, ...density.map((p) => p.count || 0));
  }, [density]);

  const totalDensityClicks = useMemo(() => {
    if (totalInteractions > 0) return totalInteractions;
    if (!Array.isArray(density) || density.length === 0) return 0;
    return density.reduce((sum, p) => sum + (p.count || 0), 0);
  }, [totalInteractions, density]);

  const scrollMilestones = useMemo(() => {
    if (!scrollReach) {
      return {
        top: 100,
        middle: 65,
        footer: 20
      };
    }
    const points = scrollReach.reachPoints || scrollReach.depthDistribution || [];
    const eligible = scrollReach.eligibleVisits || 1;

    const findRate = (targetPct, defaultRate) => {
      const pt = points.find((p) => (p.percentile ?? p.depthPercent) === targetPct);
      if (!pt) return defaultRate;
      if (pt.reachRate != null) {
        return pt.reachRate <= 1 ? Math.round(pt.reachRate * 100) : Math.round(pt.reachRate);
      }
      const count = pt.sessionCount ?? pt.reachedVisits ?? 0;
      return Math.round((count / eligible) * 100);
    };

    return {
      top: findRate(25, 100),
      middle: findRate(50, 65),
      footer: findRate(100, 20)
    };
  }, [scrollReach]);

  const hoverElements = useMemo(() => {
    return Array.isArray(hoverDwell?.elements) ? hoverDwell.elements : [];
  }, [hoverDwell]);

  const resolvedHoverElements = useMemo(() => {
    void iframeLoadedVersion;
    const doc = iframeRef.current?.contentDocument;
    return hoverElements.slice(0, 10).map((el, idx) => {
      const coords = resolveElementCoordinates(doc, el, nativeWidth, contentHeight);
      return {
        ...el,
        coords,
        fallbackTop: `${12 + idx * 8}%`,
        fallbackLeft: `${15 + (idx % 3) * 28}%`
      };
    });
  }, [hoverElements, iframeLoadedVersion, nativeWidth, contentHeight]);

  const attentionSections = useMemo(() => {
    return Array.isArray(attention?.sections) ? attention.sections : [];
  }, [attention]);

  const { matchedElements, unmatchedElements } = useMemo(() => {
    void iframeLoadedVersion;
    void scrollVersion;
    if (!Array.isArray(density) || density.length === 0) {
      return { matchedElements: [], unmatchedElements: [] };
    }

    const doc = iframeRef.current?.contentDocument;
    const matched = [];
    const unmatched = [];

    const sortedDensity = [...density].sort((a, b) => (b.count || 0) - (a.count || 0));

    sortedDensity.forEach((item, index) => {
      const rawSelector = item.elementSelector || item.selector;
      const pureSelector = rawSelector ? String(rawSelector).split('|')[0].trim() : null;
      const count = item.count || 0;
      const rank = index + 1;
      const intensity = maxCount > 0 ? count / maxCount : 0;
      const percentage = totalDensityClicks > 0
        ? ((count / totalDensityClicks) * 100).toFixed(1)
        : '0.0';

      let tier = 'cool';
      if (intensity >= 0.6) {
        tier = 'hot';
      } else if (intensity >= 0.25) {
        tier = 'warm';
      }

      const coords = resolveElementCoordinates(doc, item, nativeWidth, contentHeight);

      if (coords && coords.matched && coords.width > 0 && coords.height > 0) {
        const offsetXRatio = parseRatio(item.offsetXRatio);
        const offsetYRatio = parseRatio(item.offsetYRatio);

        const vRect = coords.viewportRect;
        const isClipped = Boolean(coords.isClipped);

        const boxLeft = vRect ? Math.round(vRect.left) : Math.round(coords.x - (coords.width * offsetXRatio));
        const boxTop = vRect ? Math.round(vRect.top) : Math.round(coords.y - (coords.height * offsetYRatio));

        matched.push({
          ...item,
          rank,
          count,
          percentage,
          intensity,
          tier,
          pureSelector,
          boxLeft,
          boxTop,
          boxWidth: vRect ? Math.round(vRect.width) : coords.width,
          boxHeight: vRect ? Math.round(vRect.height) : coords.height,
          clickX: coords.x,
          clickY: coords.y,
          isClipped
        });
      } else {
        unmatched.push({
          ...item,
          rank,
          count,
          percentage,
          intensity,
          tier,
          pureSelector,
          coords
        });
      }
    });

    return { matchedElements: matched, unmatchedElements: unmatched };
  }, [density, iframeLoadedVersion, scrollVersion, nativeWidth, contentHeight, maxCount, totalDensityClicks]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (mode === 'density') {
      // Element-based heatmap renders directly via DOM overlay in JSX with Tailwind CSS.
      // Canvas bypasses Cartesian radial blur painting.
      return;
    }

    if (mode === 'scroll') {
      const h = canvas.height;
      const w = canvas.width;

      const bands = [
        { start: 0, end: 0.25, color1: 'rgba(16, 185, 129, 0.25)', color2: 'rgba(59, 130, 246, 0.22)' },
        { start: 0.25, end: 0.50, color1: 'rgba(59, 130, 246, 0.22)', color2: 'rgba(234, 179, 8, 0.20)' },
        { start: 0.50, end: 0.75, color1: 'rgba(234, 179, 8, 0.20)', color2: 'rgba(249, 115, 22, 0.20)' },
        { start: 0.75, end: 1.00, color1: 'rgba(249, 115, 22, 0.20)', color2: 'rgba(239, 68, 68, 0.22)' }
      ];

      bands.forEach((b) => {
        const y1 = b.start * h;
        const y2 = b.end * h;
        const grad = ctx.createLinearGradient(0, y1, 0, y2);
        grad.addColorStop(0, b.color1);
        grad.addColorStop(1, b.color2);
        ctx.fillStyle = grad;
        ctx.fillRect(0, y1, w, y2 - y1);

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.lineWidth = 1;
        if (typeof ctx.setLineDash === 'function') ctx.setLineDash([6, 4]);
        if (typeof ctx.beginPath === 'function') ctx.beginPath();
        if (typeof ctx.moveTo === 'function') ctx.moveTo(0, y2);
        if (typeof ctx.lineTo === 'function') ctx.lineTo(w, y2);
        if (typeof ctx.stroke === 'function') ctx.stroke();
        if (typeof ctx.setLineDash === 'function') ctx.setLineDash([]);
      });
    }

    if (mode === 'attention' && attentionSections.length > 0) {
      const h = canvas.height;
      const w = canvas.width;
      const count = attentionSections.length;
      const sectionHeight = h / count;

      attentionSections.forEach((sec, idx) => {
        const y1 = idx * sectionHeight;
        const score = sec.attentionScore || 50;
        const alpha = Math.min(0.35, Math.max(0.10, score / 250));

        ctx.fillStyle = `rgba(234, 88, 12, ${alpha})`;
        ctx.fillRect(0, y1, w, sectionHeight);

        ctx.strokeStyle = 'rgba(249, 115, 22, 0.4)';
        ctx.lineWidth = 1;
        if (typeof ctx.beginPath === 'function') ctx.beginPath();
        if (typeof ctx.moveTo === 'function') ctx.moveTo(0, y1 + sectionHeight);
        if (typeof ctx.lineTo === 'function') ctx.lineTo(w, y1 + sectionHeight);
        if (typeof ctx.stroke === 'function') ctx.stroke();
      });
    }
  }, [mode, density, radius, blur, maxCount, palette, nativeWidth, contentHeight, attentionSections]);

  const handleMouseMove = () => {
    // Non-element coordinate hover is superseded by DOM element hover
  };

  const handleMouseLeave = () => {
    setActiveHoveredElement(null);
  };

  const hasDensityPoints = Array.isArray(density) && density.length > 0;

  return (
    <div className="heatmap-canvas-container" ref={containerRef} data-testid="heatmap-canvas-container">
      <div
        className="heatmap-viewport"
        style={{
          width: '100%',
          height: `${nativeHeight * scale}px`,
          overflow: 'hidden'
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div
          ref={stageRef}
          className="heatmap-stage"
          style={{
            width: `${nativeWidth}px`,
            height: `${nativeHeight}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            position: 'relative',
            flexShrink: 0
          }}
        >
          <div
            className="heatmap-background-preview"
            style={{ width: `${nativeWidth}px`, height: `${nativeHeight}px` }}
          >
            {hasSnapshot ? (
              unrolledHtml ? (
                <iframe
                  ref={iframeRef}
                  key={iframeKey}
                  srcDoc={unrolledHtml}
                  sandbox="allow-same-origin"
                  title="Mirrored Page Snapshot"
                  className="heatmap-iframe-preview"
                  data-testid="heatmap-iframe-preview"
                  style={{ width: `${nativeWidth}px`, height: `${nativeHeight}px` }}
                />
              ) : effectiveUrl ? (
                <iframe
                  ref={iframeRef}
                  key={iframeKey}
                  src={effectiveUrl}
                  sandbox="allow-same-origin"
                  title="Page Snapshot Preview"
                  className="heatmap-iframe-preview"
                  data-testid="heatmap-iframe-preview"
                  style={{ width: `${nativeWidth}px`, height: `${nativeHeight}px` }}
                />
              ) : backgroundImage ? (
                <img src={backgroundImage} alt="Page Preview" className="heatmap-bg-img" />
              ) : null
            ) : (
              <div className="heatmap-snapshot-fallback" data-testid="snapshot-empty-state">
                <FaCamera className="empty-icon" />
                <h4>No snapshot captured yet for this page.</h4>
                <p>Awaiting extension snapshot upload for this page.</p>
              </div>
            )}
          </div>

          <div
            ref={overlayLayerRef}
            className="heatmap-overlay-layer"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${nativeWidth}px`,
              height: `${contentHeight}px`,
              pointerEvents: 'none',
              zIndex: 2,
              willChange: 'transform'
            }}
          >
            <canvas
              ref={canvasRef}
              width={nativeWidth}
              height={contentHeight}
              className={`heatmap-overlay-canvas ${isInteractiveMode ? 'interactive-app' : ''}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${nativeWidth}px`,
                height: `${contentHeight}px`,
                opacity: opacity,
                pointerEvents: isInteractiveMode ? 'auto' : 'none'
              }}
              data-testid="heatmap-canvas-element"
            />

            {mode === 'density' && (
              <div
                className="absolute inset-0 pointer-events-none z-10"
                data-testid="element-heatmap-overlay"
              >
                {/* Matched Element Highlight Boxes with Floating Badges */}
                {matchedElements.map((el, idx) => {
                  if (el.isClipped) return null;
                  const isHovered = activeHoveredElement?.pureSelector === el.pureSelector;
                  return (
                    <div
                      key={el.id || el.pureSelector || idx}
                      className={clsx(
                        "absolute transition-all duration-150 rounded pointer-events-auto cursor-pointer border-2",
                        isInteractiveMode && "pointer-events-none opacity-40",
                        el.tier === 'hot' && "border-rose-500 bg-rose-500/20 hover:bg-rose-500/35 shadow-[0_0_12px_rgba(244,63,94,0.35)]",
                        el.tier === 'warm' && "border-amber-500 bg-amber-500/20 hover:bg-amber-500/35 shadow-[0_0_10px_rgba(245,158,11,0.3)]",
                        el.tier === 'cool' && "border-blue-500 bg-blue-500/15 hover:bg-blue-500/30 shadow-[0_0_8px_rgba(59,130,246,0.25)]",
                        isHovered && "ring-2 ring-white ring-offset-1 z-30 scale-[1.01]"
                      )}
                      style={{
                        left: `${Math.max(0, el.boxLeft)}px`,
                        top: `${Math.max(0, el.boxTop)}px`,
                        width: `${Math.max(16, el.boxWidth)}px`,
                        height: `${Math.max(16, el.boxHeight)}px`,
                        zIndex: isHovered ? 40 : 10 + el.rank
                      }}
                      onMouseEnter={() => setActiveHoveredElement(el)}
                      onMouseLeave={() => setActiveHoveredElement(null)}
                      data-testid="element-heat-box"
                      data-selector={el.pureSelector}
                    >
                      {/* Floating Click Badge */}
                      <div
                        className={clsx(
                          "absolute -top-3 -right-2 transform translate-x-1/4 -translate-y-1/4",
                          "flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold font-mono shadow-md",
                          "border border-white/30 backdrop-blur-sm select-none transition-transform hover:scale-110",
                          el.tier === 'hot' && "bg-rose-600 text-white shadow-rose-900/50",
                          el.tier === 'warm' && "bg-amber-500 text-slate-950 shadow-amber-900/50 font-extrabold",
                          el.tier === 'cool' && "bg-blue-600 text-white shadow-blue-900/50"
                        )}
                        data-testid="element-click-badge"
                      >
                        <span>{el.count.toLocaleString()}</span>
                        <span className="opacity-80 text-[9px]">({el.percentage}%)</span>
                      </div>
                    </div>
                  );
                })}

                {/* Fallback pins for density points when no DOM element was matched */}
                {matchedElements.length === 0 && unmatchedElements.map((pt, idx) => {
                  const x = pt.coords ? pt.coords.x : (pt.xPercent / 100) * nativeWidth;
                  const y = pt.coords ? pt.coords.y : (pt.yPercent / 100) * contentHeight;
                  return (
                    <div
                      key={idx}
                      className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-pointer z-20"
                      style={{ left: `${x}px`, top: `${y}px` }}
                      onMouseEnter={() => setActiveHoveredElement(pt)}
                      onMouseLeave={() => setActiveHoveredElement(null)}
                      data-testid="fallback-density-pin"
                    >
                      <div
                        className={clsx(
                          "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold font-mono shadow-lg border border-white/40",
                          pt.tier === 'hot' && "bg-rose-600 text-white shadow-rose-900/50",
                          pt.tier === 'warm' && "bg-amber-500 text-slate-950 shadow-amber-900/50",
                          pt.tier === 'cool' && "bg-blue-600 text-white shadow-blue-900/50"
                        )}
                      >
                        <span>{pt.count.toLocaleString()}</span>
                        <span className="text-[10px] opacity-80">({pt.percentage}%)</span>
                      </div>
                    </div>
                  );
                })}

                {/* Interactive Hover Popover Tooltip */}
                {activeHoveredElement && !activeHoveredElement.isClipped && (
                  <div
                    className="absolute z-50 pointer-events-none transition-all duration-75"
                    style={{
                      left: `${Math.min(
                        Math.max(10, (activeHoveredElement.boxLeft ?? (activeHoveredElement.coords?.x || 100)) + ((activeHoveredElement.boxWidth || 0) / 2) - 130),
                        nativeWidth - 280
                      )}px`,
                      top: `${(activeHoveredElement.boxTop ?? (activeHoveredElement.coords?.y || 200)) > 160
                        ? (activeHoveredElement.boxTop ?? (activeHoveredElement.coords?.y || 200)) - 140
                        : (activeHoveredElement.boxTop ?? (activeHoveredElement.coords?.y || 200)) + (activeHoveredElement.boxHeight || 24) + 10}px`
                    }}
                    data-testid="element-hover-popover"
                  >
                    <div className="w-64 p-3 rounded-xl bg-slate-950/95 text-slate-100 border border-slate-700/80 shadow-2xl backdrop-blur-md">
                      <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">
                            #{activeHoveredElement.rank}
                          </span>
                          <span className="text-xs font-semibold text-slate-200 truncate max-w-[120px]">
                            {activeHoveredElement.targetName || activeHoveredElement.friendlyName || 'Element'}
                          </span>
                        </div>
                        <span
                          className={clsx(
                            "text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider",
                            activeHoveredElement.tier === 'hot' && "bg-rose-500/20 text-rose-400 border border-rose-500/30",
                            activeHoveredElement.tier === 'warm' && "bg-amber-500/20 text-amber-400 border border-amber-500/30",
                            activeHoveredElement.tier === 'cool' && "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                          )}
                        >
                          {activeHoveredElement.tier === 'hot' ? '🔥 Hot' : activeHoveredElement.tier === 'warm' ? '⚡ Warm' : '❄️ Cool'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-2.5">
                        <div className="bg-slate-900/90 rounded-lg p-2 border border-slate-800/80">
                          <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Clicks</div>
                          <div className="text-base font-bold text-white font-mono">
                            {activeHoveredElement.count.toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-slate-900/90 rounded-lg p-2 border border-slate-800/80">
                          <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Share</div>
                          <div className="text-base font-bold text-emerald-400 font-mono">
                            {activeHoveredElement.percentage}%
                          </div>
                        </div>
                      </div>

                      {activeHoveredElement.pureSelector && (
                        <div className="mt-1">
                          <div className="text-[10px] font-medium text-slate-400 mb-1">DOM Selector</div>
                          <div className="font-mono text-[11px] bg-slate-900/90 px-2 py-1 rounded text-slate-300 border border-slate-800 break-all select-all">
                            {activeHoveredElement.pureSelector}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Off-Screen / Collapsed Interactions Tray */}
                {matchedElements.length > 0 && unmatchedElements.length > 0 && (
                  <div
                    className="absolute bottom-4 right-4 z-40 pointer-events-auto"
                    data-testid="offscreen-interactions-tray"
                  >
                    {!isOffscreenTrayOpen ? (
                      <button
                        type="button"
                        onClick={() => setIsOffscreenTrayOpen(true)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/95 hover:bg-slate-800 text-slate-200 border border-slate-700/80 shadow-xl backdrop-blur-md text-xs font-medium cursor-pointer transition-all hover:scale-105 active:scale-95"
                        data-testid="offscreen-tray-trigger"
                      >
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        <span>{unmatchedElements.length} Off-Screen / Hidden Interaction{unmatchedElements.length > 1 ? 's' : ''}</span>
                        <span className="text-[10px] bg-slate-800 px-1.5 py-0.5 rounded text-slate-400">View</span>
                      </button>
                    ) : (
                      <div
                        className="w-80 max-h-72 flex flex-col rounded-xl bg-slate-950/95 text-slate-200 border border-slate-700 shadow-2xl backdrop-blur-md overflow-hidden"
                        data-testid="offscreen-tray-panel"
                      >
                        <div className="flex items-center justify-between px-3 py-2.5 bg-slate-900/80 border-b border-slate-800">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-400" />
                            <span className="text-xs font-semibold text-slate-100">
                              Off-Screen Interactions ({unmatchedElements.length})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsOffscreenTrayOpen(false)}
                            className="text-xs text-slate-400 hover:text-slate-100 px-1.5 py-0.5 rounded hover:bg-slate-800 transition-colors"
                            data-testid="offscreen-tray-close"
                          >
                            ✕
                          </button>
                        </div>

                        <div className="overflow-y-auto p-2 space-y-1.5 flex-1 divide-y divide-slate-800/50">
                          {unmatchedElements.map((item, idx) => (
                            <div
                              key={item.pureSelector || idx}
                              className="pt-1.5 first:pt-0 flex items-center justify-between text-xs hover:bg-slate-900/60 p-1.5 rounded transition-colors"
                            >
                              <div className="flex-1 min-w-0 pr-2">
                                <div className="font-medium text-slate-200 truncate">
                                  {item.targetName || item.pureSelector || 'Dynamic Target'}
                                </div>
                                {item.pureSelector && (
                                  <div className="font-mono text-[10px] text-slate-500 truncate">
                                    {item.pureSelector}
                                  </div>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="font-bold text-white font-mono">{item.count} clicks</div>
                                <div className="text-[10px] text-slate-400">{item.percentage}%</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {mode === 'scroll' && (
              <div className="scroll-reach-badges-overlay" data-testid="scroll-reach-badges-overlay">
                <div className="scroll-dropoff-badge top" style={{ top: '15%' }}>
                  <span className="badge-text">Top: {scrollMilestones.top}% visitors</span>
                </div>
                <div className="scroll-dropoff-badge middle" style={{ top: '50%' }}>
                  <span className="badge-text">Middle: {scrollMilestones.middle}% visitors reached</span>
                </div>
                <div className="scroll-dropoff-badge footer" style={{ top: '88%' }}>
                  <span className="badge-text">Footer: {scrollMilestones.footer}% visitors reached</span>
                </div>
              </div>
            )}

            {mode === 'hover' && hoverElements.length > 0 && (
              <div className="hover-dwell-overlay" data-testid="hover-dwell-overlay">
                {resolvedHoverElements.map((el, idx) => {
                  const style = el.coords
                    ? {
                        top: `${Math.max(0, el.coords.y)}px`,
                        left: `${Math.min(el.coords.x, Math.max(0, nativeWidth - 190))}px`
                      }
                    : { top: el.fallbackTop, left: el.fallbackLeft };
                  const dwellSeconds = ((el.medianDwellMs || 0) / 1000).toFixed(2);
                  return (
                    <div
                      key={el.selector || el.elementSelector || idx}
                      className="hover-element-box"
                      style={style}
                      title={`${el.targetName || el.selector}: ${dwellSeconds}s dwell`}
                    >
                      <span className="hover-box-tag">Target: {el.selector || el.elementSelector}</span>
                      <span className="hover-box-dwell">{dwellSeconds}s dwell</span>
                    </div>
                  );
                })}
              </div>
            )}

            {mode === 'attention' && attentionSections.length > 0 && (
              <div className="section-attention-overlay" data-testid="section-attention-overlay">
                {attentionSections.map((sec, idx) => {
                  const count = attentionSections.length;
                  const heightPct = 100 / count;
                  const topPct = idx * heightPct;
                  const timeDisplay =
                    sec.avgViewTimeSeconds != null
                      ? `${sec.avgViewTimeSeconds.toFixed(1)}s`
                      : `${((sec.medianAttentionMs || 0) / 1000).toFixed(1)}s`;
                  return (
                    <div
                      key={sec.sectionId || idx}
                      className="section-shade-band-wrapper"
                      style={{ top: `${topPct}%`, height: `${heightPct}%` }}
                    >
                      <div className="section-shade-label">
                        <strong>Section: {sec.sectionLabel || sec.sectionId}</strong>
                        <span>{timeDisplay} exposure</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {isLoading && (
            <div className="heatmap-overlay-status loading">
              <div className="spinner" />
              <span>Loading density data...</span>
            </div>
          )}

          {mode === 'density' && !isLoading && !hasDensityPoints && (
            <div className="heatmap-overlay-status empty" data-testid="heatmap-empty-state">
              <FaMousePointer className="empty-icon" />
              <h4>No interactions recorded for this date range</h4>
              <p>No interactions recorded for this date range (No click coordinates recorded).</p>
            </div>
          )}
        </div>
      </div>

      {mode === 'density' && (
        <div
          className="heatmap-canvas-legend mt-2 flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-slate-900/90 text-slate-300 border border-slate-800 rounded-lg shadow-sm text-xs"
          data-testid="heatmap-canvas-legend"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-blue-500 bg-blue-500/30 shadow-[0_0_6px_rgba(59,130,246,0.4)]" />
              <span className="text-[11px] text-slate-400">Cool (&lt;25%)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-amber-500 bg-amber-500/30 shadow-[0_0_6px_rgba(245,158,11,0.4)]" />
              <span className="text-[11px] text-slate-400">Warm (25-60%)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-rose-500 bg-rose-500/30 shadow-[0_0_6px_rgba(244,63,94,0.4)]" />
              <span className="text-[11px] text-slate-400">Hot (&gt;60%)</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="legend-label text-[11px] text-slate-400">Low Activity</span>
            <div className="w-24 h-2 rounded-full bg-gradient-to-r from-blue-500 via-amber-400 to-rose-500" />
            <span className="legend-label text-[11px] text-slate-300 font-semibold">High Activity ({maxCount} max)</span>
          </div>
        </div>
      )}
    </div>
  );
}

HeatmapCanvas.propTypes = {
  density: PropTypes.arrayOf(PropTypes.shape({
    xPercent: PropTypes.number.isRequired,
    yPercent: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired
  })),
  radius: PropTypes.number,
  blur: PropTypes.number,
  opacity: PropTypes.number,
  backgroundImage: PropTypes.string,
  targetAppUrl: PropTypes.string,
  htmlSnapshot: PropTypes.string,
  snapshot: PropTypes.shape({
    url: PropTypes.string,
    htmlSnapshot: PropTypes.string,
    pageWidth: PropTypes.number,
    pageHeight: PropTypes.number,
    viewportWidth: PropTypes.number,
    viewportHeight: PropTypes.number
  }),
  mode: PropTypes.oneOf(['density', 'scroll', 'hover', 'attention']),
  scrollReach: PropTypes.object,
  hoverDwell: PropTypes.object,
  attention: PropTypes.object,
  totalInteractions: PropTypes.number,
  isInteractiveMode: PropTypes.bool,
  iframeKey: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  isLoading: PropTypes.bool
};